import React, { FC, useMemo } from "react";

import { FormFieldProps } from "../../../types";
import { useFormField } from "../../../../hooks/useFormField";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { CalendarPickerInputDropdown } from "@smartsuite/react-ui/lib";
import { DateFieldValue } from "../date.config";
import { enUS } from "date-fns/locale";

import "../date.sass";
import { format } from "date-fns";
import { validateWithYup } from "../../../validator";
import * as yup from "yup";
import { $t } from "../../../../utils/intl";
import { validatorText } from "../../../validator.text";

type DateFieldControlProps = FormFieldProps;

export const maxYearForSelectOffset = 50;
export const minYearForSelect = 1500;
export const maxYearForSelect = (): number => {
  return new Date().getFullYear() + maxYearForSelectOffset;
};

export const DateFieldControl: FC<DateFieldControlProps> = ({
  name,
  label,
  context,
  caption,
  required,
  formItem,
  readOnly,
}) => {
  const shouldShowTimePicker = useMemo(() => formItem.params?.include_time, [formItem.params]);

  const formField = useFormField<DateFieldValue>({
    name,
    validate: validateWithYup(
      yup.object({
        date: yup.mixed().when([], {
          is: () => formItem.required,
          then: (schema) => schema.required($t(validatorText.validationRequired)),
          otherwise: (schema) => schema,
        }),
        include_time: yup.boolean(),
      })
    ),
  });

  const timeFormat = useMemo(
    () => context.config.form_state.time_format ?? "24h",
    [context.config.form_state.time_format]
  );

  const handleChange = (date: Date, includeTime: boolean): void => {
    formField.onChange({
      ...formField.value,
      include_time: includeTime,
      date: date ? date.toString() : null,
    });
  };

  const maxYear = useMemo(() => maxYearForSelect(), []);

  const formattedValue = useMemo(() => {
    if (!formField.value?.date) return "";

    const timeFormatPart = timeFormat === "12h" ? "hh:mm a" : "HH:mm";

    return formField.value.include_time
      ? format(new Date(formField.value?.date), `MMMM dd, yyyy, ${timeFormatPart} (zzzz)`)
      : format(new Date(formField.value?.date), "MMMM dd, yyyy");
  }, [formField.value, timeFormat]);

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      state={formField.state}
      caption={caption}
      helpText={formItem.params.helpText}
      required={required}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      <CalendarPickerInputDropdown
        className="form-date-field"
        value={formField.value?.date ? new Date(formField.value?.date) : null}
        formattedValue={formattedValue}
        yearsOptionStart={minYearForSelect}
        yearsOptionEnd={maxYear}
        placeholder="Select Date"
        locale={enUS}
        isClearable={!readOnly}
        showArrow={false}
        timeFormat={timeFormat}
        showTimePicker={shouldShowTimePicker}
        onDateChange={handleChange}
        showIcon={!readOnly}
      />
    </FormFieldControl>
  );
};
